/* eslint-disable @typescript-eslint/no-unused-vars */

import {
  GamoraControllerContextIdentifier,
  GamoraFoundationAdapter,
  GamoraStoreDefaultNodeArgs,
  GamoraStoreDefaultNodeConfiguration,
  assert,
  debugCreate,
} from '@ver.id/libs.internal.gamora';
import { GamoraUUIDGuard } from '@ver.id/libs.internal.gamora';
import { HoruzProtocolChulakServerSessionGuard } from '../guards/server-session.js';
import { HoruzProtocolChulakStoreBaseNode } from './base.js';
import { IHoruz } from '../../../horuz.js';
const debug = debugCreate({
  namespace: 'horuz:protocols:chulak:stores:server',
});

/**
 * Args
 * @public
 */
export type HoruzProtocolChulakStoreChulakServerSessionNodeArgs<
  TRoot extends IHoruz,
> = GamoraStoreDefaultNodeArgs<GamoraFoundationAdapter, never>;

/**
 * Args
 * @public
 */
export interface HoruzProtocolChulakStoreChulakServerSessionConfiguration extends GamoraStoreDefaultNodeConfiguration {
  maxContextIdentifiersPerSession: number;
}

/**
 * @public
 */
export const HoruzProtocolChulakStoreChulakServerSessionDefaultNamespace =
  'protocols/chulak/store/server/v1';

/**
 * HoruzProtocolChulakStoreChulakServerSessionNode
 * @public
 */
export class HoruzProtocolChulakStoreChulakServerSessionNode<
  TRoot extends IHoruz,
> extends HoruzProtocolChulakStoreBaseNode<
  HoruzProtocolChulakServerSessionGuard,
  TRoot,
  never
> {
  protected _contextIdentifierByUuidMap: Map<
    string,
    Set<GamoraControllerContextIdentifier>
  >;
  protected override _configuration: HoruzProtocolChulakStoreChulakServerSessionConfiguration =
    {
      defaultLockTimeToLive: 30 * 1000,
      defaultLockWaitRetryCount: 3,
      defaultLockWaitRetryTime: 1000,
      maxContextIdentifiersPerSession: 5,
    };

  /**
   * Constructor
   */
  constructor(
    args: HoruzProtocolChulakStoreChulakServerSessionNodeArgs<TRoot>,
  ) {
    super(args);

    // Uuid map
    this._contextIdentifierByUuidMap = new Map();
  }

  /**
   * Lock resource
   */
  async lock(serverSessionUuid: GamoraUUIDGuard): Promise<boolean> {
    assert(
      serverSessionUuid instanceof GamoraUUIDGuard,
      'Invalid serverSessionUuid argument: not a GamoraUUIDGuard.',
    );
    return await this.adapter.execute(
      this.adapter.lock(
        HoruzProtocolChulakStoreChulakServerSessionDefaultNamespace,
        serverSessionUuid,
        this._configuration.defaultLockTimeToLive,
      ),
    );
  }

  /**
   * Unlock resource
   */
  async unlock(serverSessionUuid: GamoraUUIDGuard): Promise<boolean> {
    assert(
      serverSessionUuid instanceof GamoraUUIDGuard,
      'Invalid serverSessionUuid argument: not a GamoraUUIDGuard.',
    );
    return await this.adapter.execute(
      this.adapter.unlock(
        HoruzProtocolChulakStoreChulakServerSessionDefaultNamespace,
        serverSessionUuid,
      ),
    );
  }

  /**
   * Has
   */
  async has(serverSessionUuid: GamoraUUIDGuard) {
    assert(
      serverSessionUuid instanceof GamoraUUIDGuard,
      'Invalid serverSessionUuid argument: not a GamoraUUIDGuard.',
    );
    return this.adapter.execute(
      this.adapter.has(
        HoruzProtocolChulakStoreChulakServerSessionDefaultNamespace,
        serverSessionUuid,
      ),
    );
  }

  /**
   * Get
   */
  async get(
    serverSessionUuid: GamoraUUIDGuard,
  ): Promise<HoruzProtocolChulakServerSessionGuard> {
    assert(
      serverSessionUuid instanceof GamoraUUIDGuard,
      'Invalid serverSessionUuid argument: not a GamoraUUIDGuard.',
    );

    // Get protocol state
    const serverSession = await this.adapter.execute(
      this.adapter.get(
        HoruzProtocolChulakStoreChulakServerSessionDefaultNamespace,
        serverSessionUuid,
      ),
    );

    debug.log(
      '(get): Get serverSessionUuid: ' + serverSessionUuid.toJSON() + '.',
    );

    // Return
    return HoruzProtocolChulakServerSessionGuard.fromJSON(
      JSON.parse(serverSession),
    );
  }

  /**
   * Remove
   */
  async remove(serverSessionUuid: GamoraUUIDGuard): Promise<void> {
    assert(
      serverSessionUuid instanceof GamoraUUIDGuard,
      'Invalid serverSessionUuid argument: not a GamoraUUIDGuard.',
    );

    // Get protocol state
    await this.adapter.execute(
      this.adapter.remove(
        HoruzProtocolChulakStoreChulakServerSessionDefaultNamespace,
        serverSessionUuid,
      ),
    );

    debug.log(
      '(remove): Remove serverSessionUuid: ' + serverSessionUuid.toJSON() + '.',
    );
  }

  /**
   * Upsert
   */
  async upsert(
    serverSession: HoruzProtocolChulakServerSessionGuard,
  ): Promise<void> {
    assert(
      serverSession instanceof HoruzProtocolChulakServerSessionGuard,
      'Invalid serverSession argument: not a HoruzProtocolChulakServerSessionGuard.',
    );

    // Upsert
    await this.adapter.execute(
      this.adapter.upsert(
        HoruzProtocolChulakStoreChulakServerSessionDefaultNamespace,
        serverSession.uuid,
        JSON.stringify(serverSession.toJSON()),
      ),
    );

    debug.log(
      '(upsert): Upserted serverSession with uuid: ' +
        serverSession.uuid.toJSON() +
        '.',
    );
  }
}
